import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
};

const fmtDuration = (mins) => {
  if (mins == null) return "ongoing";
  if (mins < 60) return `${mins} min`;
  return `${(mins / 60).toFixed(1)} hr`;
};

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/status`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  const up = data?.state === "operational";

  return (
    <div className="min-h-screen relative noise-bg" data-testid="status-page">
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/" className="text-sm text-neutral-400 hover:text-white transition-colors">Home</Link>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        <div className="overline mb-3">SYSTEM STATUS</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
          FreightPilot AI Status
        </h1>

        {error ? (
          <div data-testid="status-banner-error" className="mt-10 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-6 py-5">
            <div className="text-lg font-semibold text-amber-300">Status temporarily unavailable</div>
            <div className="text-sm text-neutral-400 mt-1">Couldn't reach the status API — try refreshing.</div>
          </div>
        ) : !data ? (
          <div data-testid="status-loading" className="mt-10 text-neutral-500 text-sm">Checking systems…</div>
        ) : (
          <>
            <div
              data-testid="status-banner"
              className={`mt-10 flex items-center gap-4 rounded-2xl border px-6 py-5 ${
                up ? "border-emerald-500/30 bg-emerald-500/10" : "border-red-500/30 bg-red-500/10"
              }`}
            >
              <span className={`relative flex h-4 w-4`}>
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${up ? "bg-emerald-400" : "bg-red-400"}`} />
                <span className={`relative inline-flex h-4 w-4 rounded-full ${up ? "bg-emerald-400" : "bg-red-400"}`} />
              </span>
              <div>
                <div className={`text-lg font-semibold ${up ? "text-emerald-300" : "text-red-300"}`}>
                  {up ? "All systems operational" : "Service disruption in progress"}
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">Last checked {fmtDate(data.checked_at)}</div>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="status-uptime-grid">
              {[
                { label: "Uptime — last 30 days", value: `${data.uptime_30d_pct}%`, testid: "uptime-30d" },
                { label: "Uptime — last 90 days", value: `${data.uptime_90d_pct}%`, testid: "uptime-90d" },
                { label: "Incidents — last 90 days", value: data.incidents_90d, testid: "incidents-90d" },
              ].map((s) => (
                <div key={s.label} data-testid={s.testid} className="rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4">
                  <div className="text-2xl font-bold text-white">{s.value}</div>
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <h2 className="font-display text-lg font-semibold text-white mb-4">Incident history</h2>
              {data.recent_incidents.length === 0 ? (
                <div data-testid="status-no-incidents" className="rounded-xl border border-white/10 bg-white/[0.03] px-5 py-6 text-sm text-neutral-400">
                  No incidents recorded in the last 90 days. 🎉
                </div>
              ) : (
                <div className="space-y-2" data-testid="status-incident-list">
                  {data.recent_incidents.map((inc, i) => (
                    <div key={i} data-testid={`status-incident-${i}`} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-5 py-3.5">
                      <div>
                        <div className="text-sm text-white">{fmtDate(inc.started_at)}</div>
                        <div className="text-xs text-neutral-500 mt-0.5">
                          {inc.ongoing ? "Ongoing — being investigated" : `Resolved · downtime ${fmtDuration(inc.downtime_minutes)}`}
                        </div>
                      </div>
                      <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${
                        inc.ongoing ? "bg-red-500/15 text-red-300" : "bg-emerald-500/15 text-emerald-300"
                      }`}>
                        {inc.ongoing ? "Ongoing" : "Resolved"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="mt-10 text-xs text-neutral-600 leading-relaxed">
              This page is served by the same platform it monitors — if you can read this, the core
              service is reachable. Monitoring probes run every 2 minutes from an independent watcher.
            </p>
          </>
        )}
      </main>

      <footer className="relative z-10 border-t border-white/10 py-8 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} Freight Pilot AI LLC · <Link to="/privacy" className="hover:text-white">Privacy</Link> ·{" "}
        <Link to="/terms" className="hover:text-white">Terms</Link> · <Link to="/status" className="hover:text-white">Status</Link>
      </footer>
    </div>
  );
}
